// Entropy portrait of a file (DESIGN_SPEC §12.15): computed from the file's own bytes, never a canned image.
//   gridFor(size, cols) → { cols, rows, block, window, span, count }
//       span = first 64 MiB · block = max(64, ceil(span / (cols·82))) bytes per glyph · rows = 82 or fewer for small files
//       window = max(256, block): each glyph's Shannon entropy is measured over a window centred on its block, so a
//       64-byte block still reads on the 0–8 bits/byte scale the Static PE figure uses
//   blockEntropies(bytes, grid, opts) → Float32Array      time-sliced (yields to the page), abortable, reports progress
//   stretchOf(values) → { lo, hi, min, median, max }      lo = p2, hi = p98; under 0.6 apart: median ± 0.3
//   levelLines(values, grid, stretch) → string[]          round(clamp((e − lo)/(hi − lo)) × 15) on " .,:;=+xzisX#$W@"
//   rangeEntropy(bytes, start, end, tick?)                exact Shannon entropy of a byte range (section labels)
//   buildPortrait(bytes, size, cols, pe, opts) → Portrait  all of the above + PE marks (hdr, sections, overlay)
//   yieldToMain() · makeTicker(signal?, budgetMs?)
import type { PeInfo } from "./pe-sections";

export const RAMP = " .,:;=+xzisX#$W@";
export const PORTRAIT_MAX = 64 * 1024 * 1024;
const MAX_ROWS = 82;
const MIN_BLOCK = 64;
const MIN_WINDOW = 256;

type Grid = { cols: number; rows: number; block: number; window: number; span: number; count: number };

type Stretch = { lo: number; hi: number; min: number; median: number; max: number };

/** A labelled offset in the right gutter: the header region, each section's start, the overlay. */
export type PortraitMark = { label: string; offset: number; size: number; row: number; entropy: number | null };

export type Portrait = Stretch & {
  grid: Grid;
  lines: string[];
  marks: PortraitMark[];
};

export function gridFor(size: number, cols: number, maxRows = MAX_ROWS): Grid {
  const span = Math.min(Math.max(0, size), PORTRAIT_MAX);
  const block = Math.max(MIN_BLOCK, Math.ceil(span / (cols * maxRows)));
  const count = Math.ceil(span / block);
  return { cols, rows: Math.ceil(count / cols), block, window: Math.max(MIN_WINDOW, block), span, count };
}

export function abortError(): Error {
  return typeof DOMException === "function" ? new DOMException("The analysis was replaced", "AbortError") : Object.assign(new Error("aborted"), { name: "AbortError" });
}

/** Give the page a frame: scheduler.yield where it exists, else a MessageChannel macrotask (no 4 ms clamp). */
function yieldToMain(): Promise<void> {
  const s = (globalThis as { scheduler?: { yield?: () => Promise<void> } }).scheduler;
  if (typeof s?.yield === "function") return s.yield();
  if (typeof MessageChannel === "function") {
    return new Promise((resolve) => {
      const ch = new MessageChannel();
      ch.port1.onmessage = () => {
        ch.port1.close();
        resolve();
      };
      ch.port2.postMessage(0);
    });
  }
  return new Promise((resolve) => setTimeout(resolve, 0));
}

/** A cooperative time slice: call it often; it yields once `budgetMs` of work has passed and throws when aborted. */
export function makeTicker(signal?: AbortSignal, budgetMs = 12): () => Promise<void> {
  let t = performance.now();
  return async () => {
    if (signal?.aborted) throw abortError();
    if (performance.now() - t < budgetMs) return;
    await yieldToMain();
    if (signal?.aborted) throw abortError();
    t = performance.now();
  };
}

const clogCache = new Map<number, Float64Array>();
function clogTable(n: number): Float64Array {
  let t = clogCache.get(n);
  if (!t) {
    t = new Float64Array(n + 2);
    for (let c = 2; c < t.length; c++) t[c] = c * Math.log2(c);
    clogCache.set(n, t);
  }
  return t;
}

export async function blockEntropies(
  bytes: Uint8Array,
  grid: Grid,
  opts: { signal?: AbortSignal; onProgress?: (fraction: number) => void } = {},
): Promise<Float32Array> {
  const { span, block, count } = grid;
  const out = new Float32Array(count);
  if (count === 0) return out;
  const W = Math.min(grid.window, span);
  const clog = clogTable(Math.max(W, block));
  const hist = new Uint32Array(256);
  const tick = makeTicker(opts.signal);
  let s0 = 0;
  let e0 = 0;
  let sum = 0; // Σ c·log2(c) over the window's histogram
  for (let i = 0; i < count; i++) {
    const bs = i * block;
    const be = Math.min(span, bs + block);
    let s: number;
    let e: number;
    if (W >= span) {
      s = 0;
      e = span;
    } else if (block >= W) {
      s = bs;
      e = be;
    } else {
      s = Math.min(Math.max(0, Math.round((bs + be) / 2 - W / 2)), span - W);
      e = s + W;
    }
    if (i === 0 || s >= e0) {
      hist.fill(0);
      sum = 0;
      s0 = s;
      e0 = s;
    }
    for (let k = s0; k < s; k++) {
      const v = bytes[k];
      const c = hist[v];
      sum += clog[c - 1] - clog[c];
      hist[v] = c - 1;
    }
    for (let k = e0; k < e; k++) {
      const v = bytes[k];
      const c = hist[v];
      sum += clog[c + 1] - clog[c];
      hist[v] = c + 1;
    }
    s0 = s;
    e0 = e;
    const n = e - s;
    out[i] = n > 1 ? Math.max(0, Math.log2(n) - sum / n) : 0;
    if ((i & 63) === 63) {
      opts.onProgress?.(be / span);
      await tick();
    }
  }
  opts.onProgress?.(1);
  return out;
}

export function stretchOf(values: ArrayLike<number>): Stretch {
  const n = values.length;
  if (n === 0) return { lo: 0, hi: 8, min: 0, median: 0, max: 0 };
  const sorted = Float64Array.from(values).sort();
  const q = (p: number) => sorted[Math.min(n - 1, Math.max(0, Math.round(p * (n - 1))))];
  const median = q(0.5);
  let lo = q(0.02);
  let hi = q(0.98);
  if (hi - lo < 0.6) {
    lo = median - 0.3;
    hi = median + 0.3;
    if (lo < 0) {
      hi -= lo;
      lo = 0;
    }
    if (hi > 8) {
      lo -= hi - 8;
      hi = 8;
    }
  }
  return { lo, hi, min: sorted[0], median, max: sorted[n - 1] };
}

export function levelOf(e: number, lo: number, hi: number): number {
  const t = hi > lo ? (e - lo) / (hi - lo) : 0;
  return Math.round(Math.min(1, Math.max(0, t)) * (RAMP.length - 1));
}

export function levelLines(values: ArrayLike<number>, grid: Grid, s: Pick<Stretch, "lo" | "hi">): string[] {
  const lines: string[] = [];
  for (let r = 0; r < grid.rows; r++) {
    let line = "";
    const end = Math.min(values.length, (r + 1) * grid.cols);
    for (let i = r * grid.cols; i < end; i++) line += RAMP[levelOf(values[i], s.lo, s.hi)];
    lines.push(line);
  }
  return lines;
}

export async function rangeEntropy(bytes: Uint8Array, start: number, end: number, tick?: () => Promise<void>): Promise<number> {
  const a = Math.max(0, start);
  const b = Math.min(bytes.length, end);
  const n = b - a;
  if (n <= 1) return 0;
  const hist = new Float64Array(256);
  const STEP = 1 << 20;
  for (let k = a; k < b; k += STEP) {
    const stop = Math.min(b, k + STEP);
    for (let j = k; j < stop; j++) hist[bytes[j]]++;
    if (tick) await tick();
  }
  let h = 0;
  for (let v = 0; v < 256; v++) {
    const c = hist[v];
    if (c > 0) h -= (c / n) * Math.log2(c / n);
  }
  return h;
}

/** hdr at 0, one mark per section start (with that section's entropy), and the overlay past the last section. */
async function peMarks(bytes: Uint8Array, size: number, grid: Grid, pe: PeInfo, tick?: () => Promise<void>): Promise<PortraitMark[]> {
  const rowOf = (offset: number) => Math.min(grid.rows - 1, Math.floor(Math.floor(offset / grid.block) / grid.cols));
  const marks: PortraitMark[] = [{ label: "hdr", offset: 0, size: pe.headerEnd, row: 0, entropy: null }];
  for (const s of pe.sections) {
    if (s.offset >= grid.span) continue; // past the portrait window
    const entropy = await rangeEntropy(bytes, s.offset, Math.min(s.offset + s.size, size), tick);
    marks.push({ label: `${s.name} ${entropy.toFixed(2)}`, offset: s.offset, size: s.size, row: rowOf(s.offset), entropy });
  }
  const overlay = pe.end;
  if (overlay < Math.min(size, grid.span) && size - overlay >= grid.block) {
    const entropy = await rangeEntropy(bytes, overlay, size, tick);
    marks.push({ label: `overlay ${entropy.toFixed(2)}`, offset: overlay, size: size - overlay, row: rowOf(overlay), entropy });
  }
  return marks;
}

export async function buildPortrait(
  bytes: Uint8Array,
  size: number,
  cols: number,
  pe: PeInfo | null,
  opts: { signal?: AbortSignal; onProgress?: (fraction: number) => void } = {},
): Promise<Portrait> {
  const grid = gridFor(size, cols);
  const values = await blockEntropies(bytes.subarray(0, grid.span), grid, {
    signal: opts.signal,
    onProgress: (f) => opts.onProgress?.(f * (pe ? 0.8 : 1)),
  });
  const s = stretchOf(values);
  const lines = levelLines(values, grid, s);
  const tick = makeTicker(opts.signal);
  const marks = pe ? await peMarks(bytes.subarray(0, grid.span), Math.min(size, grid.span), grid, pe, tick) : [];
  opts.onProgress?.(1);
  return { ...s, grid, lines, marks };
}
